import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { disposeScene } from '../utils/ThreeCleanup';

const DriftVisualization: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<number>(0);
  const [text, setText] = useState<string>('');
  const [isTerminalVisible, setIsTerminalVisible] = useState<boolean>(true);

  const currentUpdateFnRef = useRef<((delta: number) => void) | null>(null);
  const phaseHandlersRef = useRef<{[key: number]: () => ((delta: number) => void) | void}>({});

  const textAnimationRef = useRef<number | null>(null);
  const typingPositionRef = useRef<number>(0);
  const targetTextRef = useRef<string>('');

  const animateText = (text: string, delay: number = 30, callback?: () => void) => {
    if (textAnimationRef.current !== null) {
      cancelAnimationFrame(textAnimationRef.current);
      textAnimationRef.current = null;
    }

    setText('');
    typingPositionRef.current = 0;
    targetTextRef.current = text;

    const typeNextChar = () => {
      if (typingPositionRef.current < targetTextRef.current.length) {
        const nextPosition = Math.min(typingPositionRef.current + 1, targetTextRef.current.length);
        const currentText = targetTextRef.current.substring(0, nextPosition);
        setText(currentText);

        typingPositionRef.current = nextPosition;
        textAnimationRef.current = requestAnimationFrame(() => {
          setTimeout(typeNextChar, delay);
        });
      } else {
        textAnimationRef.current = null;
        if (callback) callback();
      }
    };

    textAnimationRef.current = requestAnimationFrame(typeNextChar);
  };

  useEffect(() => {
    if (!containerRef.current) return;

    let scene: THREE.Scene;
    let camera: THREE.PerspectiveCamera;
    let renderer: THREE.WebGLRenderer;
    let clock: THREE.Clock;
    let station: THREE.Group;
    let debrisParticles: THREE.Points;
    let holographicSprites: THREE.Sprite[] = [];
    let starField: THREE.Points;
    let driftParticles: THREE.Points;
    let earthSphere: THREE.Mesh;

    const init = () => {
      scene = new THREE.Scene();
      scene.background = new THREE.Color(0x000005);
      scene.fog = new THREE.FogExp2(0x000005, 0.003);

      camera = new THREE.PerspectiveCamera(
        70,
        containerRef.current!.clientWidth / containerRef.current!.clientHeight,
        0.1,
        1000
      );
      camera.position.z = 20;
      camera.position.y = 5;

      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(containerRef.current!.clientWidth, containerRef.current!.clientHeight);
      renderer.setPixelRatio(window.devicePixelRatio);
      containerRef.current!.appendChild(renderer.domElement);

      clock = new THREE.Clock();

    };

    // Phase 1: Terminal initialization - SPACE STATION
    const startPhase1 = () => {
      setPhase(1);
      animateText("[ORBITAL v5.2]\nDocking sequence initiated...", 30, () => {
        setTimeout(() => {
          setIsTerminalVisible(false);
          startPhase2();
        }, 4000);
      });
    };

    // Phase 2: Space Station
    const startPhase2 = () => {
      setPhase(2);

      station = new THREE.Group();
      scene.add(station);

      // Central hub
      const hubGeometry = new THREE.SphereGeometry(4, 16, 16);
      const hubMaterial = new THREE.MeshPhongMaterial({
        color: 0x888899,
        emissive: new THREE.Color(0.02, 0.02, 0.04),
        specular: new THREE.Color(0.3, 0.3, 0.4),
        shininess: 80,
        transparent: true,
        opacity: 0.95
      });
      const hub = new THREE.Mesh(hubGeometry, hubMaterial);
      station.add(hub);

      // Cylindrical modules
      const moduleColors = [0x667788, 0x778899, 0x556677, 0x889999];
      for (let i = 0; i < 8; i++) {
        const radius = Math.random() * 1.5 + 0.8;
        const length = Math.random() * 15 + 5;
        const moduleGeometry = new THREE.CylinderGeometry(radius, radius, length, 12);
        const moduleMaterial = new THREE.MeshPhongMaterial({
          color: moduleColors[Math.floor(Math.random() * moduleColors.length)],
          emissive: new THREE.Color(0.01, 0.01, 0.03),
          specular: new THREE.Color(0.2, 0.2, 0.3),
          shininess: 60,
          transparent: true,
          opacity: 0.9
        });

        const module = new THREE.Mesh(moduleGeometry, moduleMaterial);

        // Arrange modules radiating from hub
        const angle = (i / 8) * Math.PI * 2;
        const dist = 8 + Math.random() * 5;
        module.position.set(
          Math.cos(angle) * dist,
          (Math.random() - 0.5) * 6,
          Math.sin(angle) * dist
        );
        module.rotation.z = angle + Math.PI / 2;
        module.rotation.x = (Math.random() - 0.5) * 0.3;

        // Add port lights
        const lightMaterial = new THREE.MeshBasicMaterial({
          color: 0x88ccff,
          transparent: true,
          opacity: 0.7
        });
        for (let l = 0; l < 4; l++) {
          const lightGeom = new THREE.SphereGeometry(0.2, 8, 8);
          const lightMesh = new THREE.Mesh(lightGeom, lightMaterial);
          lightMesh.position.set(
            (Math.random() - 0.5) * radius,
            (Math.random() - 0.5) * length * 0.8,
            radius
          );
          module.add(lightMesh);
        }

        station.add(module);
      }

      // Ring structures
      for (let r = 0; r < 3; r++) {
        const ringGeometry = new THREE.TorusGeometry(6 + r * 4, 0.5, 8, 32);
        const ringMaterial = new THREE.MeshPhongMaterial({
          color: 0x99aabb,
          emissive: new THREE.Color(0.01, 0.02, 0.03),
          shininess: 50,
          transparent: true,
          opacity: 0.85
        });
        const ring = new THREE.Mesh(ringGeometry, ringMaterial);
        ring.rotation.x = Math.PI / 2 + (Math.random() - 0.5) * 0.3;
        ring.position.y = (r - 1) * 3;
        station.add(ring);
      }

      // Earth below
      const earthGeometry = new THREE.SphereGeometry(30, 32, 32);
      const earthMaterial = new THREE.MeshPhongMaterial({
        color: 0x2244aa,
        emissive: new THREE.Color(0.02, 0.04, 0.08),
        specular: new THREE.Color(0.1, 0.2, 0.4),
        shininess: 25,
        transparent: true,
        opacity: 0.9
      });
      earthSphere = new THREE.Mesh(earthGeometry, earthMaterial);
      earthSphere.position.set(0, -45, -20);
      scene.add(earthSphere);

      // Add atmosphere glow to Earth
      const atmosphereGeometry = new THREE.SphereGeometry(31, 32, 32);
      const atmosphereMaterial = new THREE.MeshBasicMaterial({
        color: 0x4488ff,
        transparent: true,
        opacity: 0.15,
        side: THREE.BackSide
      });
      const atmosphere = new THREE.Mesh(atmosphereGeometry, atmosphereMaterial);
      atmosphere.position.copy(earthSphere.position);
      scene.add(atmosphere);

      // Floating debris particles
      const debrisCount = 5000;
      const debrisGeometry = new THREE.BufferGeometry();
      const debrisPositions = new Float32Array(debrisCount * 3);
      const debrisVelocities = new Float32Array(debrisCount);

      for (let i = 0; i < debrisCount; i++) {
        debrisPositions[i * 3] = Math.random() * 300 - 150;
        debrisPositions[i * 3 + 1] = Math.random() * 300 - 150;
        debrisPositions[i * 3 + 2] = Math.random() * 300 - 150;
        debrisVelocities[i] = 0.02 + Math.random() * 0.08;
      }

      debrisGeometry.setAttribute('position', new THREE.BufferAttribute(debrisPositions, 3));
      debrisGeometry.setAttribute('velocity', new THREE.BufferAttribute(debrisVelocities, 1));

      const debrisMaterial = new THREE.PointsMaterial({
        color: 0xaabbcc,
        size: 0.15,
        transparent: true,
        opacity: 0.4,
        blending: THREE.AdditiveBlending
      });

      debrisParticles = new THREE.Points(debrisGeometry, debrisMaterial);
      scene.add(debrisParticles);

      // Holographic sprites
      const spriteTexts = [
        "Hull Integrity: 88%",
        "Life Support: Nominal",
        "Comm Link: Degrading",
        "Orbit: Decaying",
        "Crew Status: Unknown"
      ];

      const createHoloSprite = (text: string, position: THREE.Vector3) => {
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 128;
        const ctx = canvas.getContext('2d')!;

        ctx.fillStyle = 'rgba(0, 0, 0, 0)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.font = '15px monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        ctx.shadowColor = '#88ccff';
        ctx.shadowBlur = 6;
        ctx.fillStyle = '#88ccff';
        ctx.fillText(text, canvas.width / 2, canvas.height / 2);

        const texture = new THREE.Texture(canvas);
        texture.needsUpdate = true;

        const material = new THREE.SpriteMaterial({
          map: texture,
          transparent: true,
          blending: THREE.AdditiveBlending
        });

        const sprite = new THREE.Sprite(material);
        sprite.position.copy(position);
        sprite.scale.set(10, 5, 1);

        scene.add(sprite);
        holographicSprites.push(sprite);
      };

      for (let i = 0; i < spriteTexts.length; i++) {
        const angle = (i / spriteTexts.length) * Math.PI * 2;
        const dist = 15 + Math.random() * 10;
        createHoloSprite(spriteTexts[i], new THREE.Vector3(
          Math.cos(angle) * dist,
          Math.random() * 10 + 3,
          Math.sin(angle) * dist
        ));
      }

      // Space lighting - cold white/blue
      const ambientLight = new THREE.AmbientLight(0x111122);
      scene.add(ambientLight);

      const sunLight = new THREE.DirectionalLight(0xffffff, 1.0);
      sunLight.position.set(50, 30, 20);
      scene.add(sunLight);

      const blueLight = new THREE.PointLight(0x4488ff, 0.5, 50);
      blueLight.position.set(0, -30, -10);
      scene.add(blueLight);

      for (let i = 0; i < 10; i++) {
        const pointLight = new THREE.PointLight(0x88aaff, 0.3, 15);
        const angle = (i / 10) * Math.PI * 2;
        pointLight.position.set(
          Math.cos(angle) * 12,
          (Math.random() - 0.5) * 8,
          Math.sin(angle) * 12
        );
        scene.add(pointLight);
      }

      // Camera orbits the station
      let cameraPath = new THREE.CatmullRomCurve3([
        new THREE.Vector3(0, 5, 25),
        new THREE.Vector3(-15, 8, 15),
        new THREE.Vector3(-25, 10, 0),
        new THREE.Vector3(-15, 12, -15),
        new THREE.Vector3(0, 8, -20),
        new THREE.Vector3(15, 5, -10),
        new THREE.Vector3(20, 4, 10),
        new THREE.Vector3(0, 5, 25),
      ]);
      let cameraPathProgress = 0;

      setTimeout(() => {
        setText('');
        animateText("Ground control has been silent for 47 days.", 40);
        setTimeout(() => { startPhase3(); }, 10000);
      }, 5000);

      return (delta: number) => {
        // Debris drifts slowly
        const positions = debrisParticles.geometry.attributes.position.array as Float32Array;
        const velocities = debrisParticles.geometry.attributes.velocity.array as Float32Array;

        for (let i = 0; i < debrisCount; i++) {
          positions[i * 3] += Math.sin(Date.now() * 0.0003 + i) * velocities[i] * delta * 5;
          positions[i * 3 + 1] += Math.cos(Date.now() * 0.0004 + i * 0.7) * velocities[i] * delta * 3;
          positions[i * 3 + 2] += Math.sin(Date.now() * 0.0002 + i * 1.3) * velocities[i] * delta * 4;
        }
        debrisParticles.geometry.attributes.position.needsUpdate = true;

        // Station rotates slowly
        station.rotation.y += 0.002 * delta;

        cameraPathProgress += 0.04 * delta;
        if (cameraPathProgress > 1) cameraPathProgress = 0;
        const camPos = cameraPath.getPoint(cameraPathProgress);
        camera.position.copy(camPos);
        camera.lookAt(0, 0, 0);

        holographicSprites.forEach((sprite, idx) => {
          sprite.lookAt(camera.position);
          const time = Date.now() * 0.001;
          sprite.position.y += Math.sin(time + idx * 2) * 0.005;
        });

        // Earth rotates slowly
        if (earthSphere) {
          earthSphere.rotation.y += 0.001 * delta;
        }
      };
    };

    // Phase 3: Station breaks apart, modules drift away
    const startPhase3 = () => {
      setPhase(3);
      setText('');
      animateText("> hull: critical failure\n> modules: detaching\n> /station breakup in progress...", 40);

      // Store original positions for drift
      const moduleOffsets: THREE.Vector3[] = [];
      station.children.forEach(_child => {
        const dir = new THREE.Vector3(
          (Math.random() - 0.5) * 2,
          (Math.random() - 0.5) * 2,
          (Math.random() - 0.5) * 2
        ).normalize();
        moduleOffsets.push(dir);
      });

      let breakProgress = 0;
      const breakInterval = setInterval(() => {
        breakProgress += 0.01;

        // Modules drift apart
        station.children.forEach((module: THREE.Object3D, idx) => {
          if (module instanceof THREE.Mesh) {
            const drift = moduleOffsets[idx] || new THREE.Vector3(1, 0, 0);
            module.position.x += drift.x * breakProgress * 0.3;
            module.position.y += drift.y * breakProgress * 0.3;
            module.position.z += drift.z * breakProgress * 0.3;

            // Tumble
            module.rotation.x += (Math.random() - 0.5) * breakProgress * 0.02;
            module.rotation.z += (Math.random() - 0.5) * breakProgress * 0.02;

            if (module.material instanceof THREE.MeshPhongMaterial) {
              module.material.opacity = Math.max(0.2, 1 - breakProgress * 0.8);
            }
          }
        });

        // Sprites flicker and drift
        holographicSprites.forEach(sprite => {
          if (Math.random() > 0.92) sprite.visible = !sprite.visible;
          if (Math.random() > 0.9) {
            sprite.position.x += (Math.random() - 0.5) * breakProgress * 3;
            sprite.position.y += (Math.random() - 0.5) * breakProgress * 3;
          }
        });

        // Camera shake
        if (Math.random() > 0.88) {
          camera.position.x += (Math.random() - 0.5) * breakProgress * 0.3;
          camera.position.y += (Math.random() - 0.5) * breakProgress * 0.2;
          camera.rotation.z = (Math.random() - 0.5) * breakProgress * 0.05;
        }

        if (breakProgress >= 1) {
          clearInterval(breakInterval);
          startPhase4();
        }
      }, 100);

      return (delta: number) => {
        // Debris increases
        const positions = debrisParticles.geometry.attributes.position.array as Float32Array;
        const velocities = debrisParticles.geometry.attributes.velocity.array as Float32Array;

        for (let i = 0; i < positions.length / 3; i++) {
          const speed = velocities[i] * (1 + breakProgress * 5);
          positions[i * 3] += Math.sin(Date.now() * 0.001 + i) * speed * delta * 3;
          positions[i * 3 + 1] += Math.cos(Date.now() * 0.001 + i * 0.7) * speed * delta * 2;
          positions[i * 3 + 2] += Math.sin(Date.now() * 0.0008 + i * 1.3) * speed * delta * 3;
        }
        debrisParticles.geometry.attributes.position.needsUpdate = true;

        station.rotation.y += 0.005 * delta * (1 + breakProgress);
      };
    };

    // Phase 4: Alone in space with distant stars
    const startPhase4 = () => {
      setPhase(4);

      while (scene.children.length > 0) {
        scene.remove(scene.children[0]);
      }

      // Vast starfield
      const starsCount = 4000;
      const starsGeometry = new THREE.BufferGeometry();
      const starPositions = new Float32Array(starsCount * 3);
      for (let i = 0; i < starsCount * 3; i += 3) {
        starPositions[i] = (Math.random() - 0.5) * 3000;
        starPositions[i + 1] = (Math.random() - 0.5) * 3000;
        starPositions[i + 2] = (Math.random() - 0.5) * 3000;
      }
      starsGeometry.setAttribute('position', new THREE.BufferAttribute(starPositions, 3));
      const starMaterial = new THREE.PointsMaterial({
        color: 0xffffff,
        size: 0.7,
        transparent: true,
        opacity: 0.9
      });
      starField = new THREE.Points(starsGeometry, starMaterial);
      scene.add(starField);

      // Very few slow-drifting debris particles (remnants of station)
      const driftCount = 300;
      const driftGeometry = new THREE.BufferGeometry();
      const driftPositions = new Float32Array(driftCount * 3);
      const driftVelocities = new Float32Array(driftCount);

      for (let i = 0; i < driftCount; i++) {
        driftPositions[i * 3] = (Math.random() - 0.5) * 80;
        driftPositions[i * 3 + 1] = (Math.random() - 0.5) * 80;
        driftPositions[i * 3 + 2] = (Math.random() - 0.5) * 80;
        driftVelocities[i] = 0.05 + Math.random() * 0.1;
      }

      driftGeometry.setAttribute('position', new THREE.BufferAttribute(driftPositions, 3));
      driftGeometry.setAttribute('velocity', new THREE.BufferAttribute(driftVelocities, 1));

      const driftMaterial = new THREE.PointsMaterial({
        color: 0x8899aa,
        size: 0.4,
        transparent: true,
        opacity: 0.5,
        blending: THREE.AdditiveBlending
      });

      driftParticles = new THREE.Points(driftGeometry, driftMaterial);
      scene.add(driftParticles);

      setTimeout(() => {
        setText('');
        animateText("\"The silence between stars is the loudest sound.\"", 50, () => {
          setTimeout(() => {
            const fadeOut = setInterval(() => {
              if (starMaterial.opacity > 0.01) {
                starMaterial.opacity -= 0.008;
                driftMaterial.opacity -= 0.005;
              } else {
                clearInterval(fadeOut);
                setTimeout(() => { setText(''); }, 3000);
              }
            }, 50);
          }, 5000);
        });
      }, 2000);

      return (delta: number) => {
        if (starField) {
          starField.rotation.y += 0.0002;
          starField.rotation.x += 0.0001;
        }

        if (driftParticles) {
          const positions = driftParticles.geometry.attributes.position.array as Float32Array;
          const velocities = driftParticles.geometry.attributes.velocity.array as Float32Array;

          for (let i = 0; i < driftCount; i++) {
            const time = Date.now() * 0.0003;
            positions[i * 3] += Math.sin(time + i * 0.5) * velocities[i] * delta;
            positions[i * 3 + 1] += Math.cos(time + i * 0.3) * velocities[i] * delta * 0.5;
            positions[i * 3 + 2] += Math.sin(time * 0.7 + i) * velocities[i] * delta;

            // Slow outward drift
            positions[i * 3] += velocities[i] * delta * 0.2;
          }
          driftParticles.geometry.attributes.position.needsUpdate = true;
        }
      };
    };

    init();

    phaseHandlersRef.current = {
      1: startPhase1,
      2: startPhase2,
      3: startPhase3,
      4: startPhase4
    };

    setTimeout(() => { startPhase1(); }, 1000);

    let animationFrameId = 0;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      const delta = clock.getDelta();
      if (currentUpdateFnRef.current) {
        currentUpdateFnRef.current(delta);
      }
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      if (!containerRef.current) return;
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
      if (containerRef.current && containerRef.current.contains(renderer.domElement)) {
        containerRef.current.removeChild(renderer.domElement);
      }
      disposeScene(scene, renderer);
      if (textAnimationRef.current !== null) {
        cancelAnimationFrame(textAnimationRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (phase > 0 && phaseHandlersRef.current[phase]) {
      currentUpdateFnRef.current = phaseHandlersRef.current[phase]() || null;
    }
  }, [phase]);

  useEffect(() => {
    return () => {
      if (textAnimationRef.current !== null) {
        cancelAnimationFrame(textAnimationRef.current);
      }
    };
  }, []);

  return (
    <div className="w-full h-full">
      <div className="relative w-full h-full bg-black overflow-hidden" ref={containerRef}>
        {isTerminalVisible && (
          <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
            <div className="terminal-text text-[var(--terminal-text)] p-8 font-mono whitespace-pre-line">
              {text}
              <span className="cursor-blink">█</span>
            </div>
          </div>
        )}

        {!isTerminalVisible && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="terminal-text text-center p-8 text-[var(--terminal-text)] font-mono whitespace-pre-line max-w-2xl">
              {text}
              {text && <span className="cursor-blink">█</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriftVisualization;